import { useEffect, useState, type FormEvent } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { uploadProductImage, uploadProductVideo } from '@/services/products';
import { useProducts } from '@/hooks/useProducts';
import {
  fetchAllTestimonials,
  fetchTestimonialsSection,
  saveTestimonialsSection,
  saveTestimonial,
  deleteTestimonial,
  reorderTestimonials,
  DEFAULT_TESTIMONIAL_SECTION,
  type Testimonial,
  type TestimonialInput,
  type TestimonialSection,
} from '@/services/testimonials';

const emptyTestimonial = (order: number): TestimonialInput => ({
  mediaType: 'image',
  mediaUrl: '',
  caption: '',
  clientName: '',
  productName: '',
  productSlug: '',
  reply: '¡Bienvenido a la manada! Gracias por confiar en Paticas con Propósito.',
  displayOrder: order,
  isActive: true,
});

export default function AdminTestimoniosPage() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [editing, setEditing] = useState<TestimonialInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'active' | 'hidden'>('all');
  const { data: products } = useProducts();

  const [section, setSection] = useState<TestimonialSection>(DEFAULT_TESTIMONIAL_SECTION);
  const [savingSection, setSavingSection] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [rows, cfg] = await Promise.all([fetchAllTestimonials(), fetchTestimonialsSection()]);
      setItems(rows);
      setSection(cfg);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los testimonios.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleSectionSave() {
    setSavingSection(true);
    setFeedback('');
    const { error: err } = await saveTestimonialsSection(section);
    setSavingSection(false);
    if (err) {
      setError(err);
      return;
    }
    setFeedback('Encabezado de la sección guardado.');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setFormError('');
    const { error: err } = await saveTestimonial(editing);
    setSaving(false);
    if (err) {
      setFormError(err);
      return;
    }
    setEditing(null);
    setFeedback('Testimonio guardado correctamente.');
    await load();
  }

  async function handleDelete(id: string, mediaUrl: string) {
    const { error: err } = await deleteTestimonial(id, mediaUrl);
    if (err) {
      setError(err);
      return;
    }
    setFeedback('Testimonio eliminado.');
    await load();
  }

  async function move(id: string, dir: -1 | 1) {
    const idx = items.findIndex((f) => f.id === id);
    const target = idx + dir;
    if (idx < 0 || target < 0 || target >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setItems(reordered);
    await reorderTestimonials(reordered.map((f) => f.id));
  }

  async function handleMediaUpload(e: React.ChangeEvent<HTMLInputElement>, kind: 'image' | 'video') {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploading(true);
    setFormError('');
    const { url, error: err } = kind === 'video'
      ? await uploadProductVideo(file)
      : await uploadProductImage(file);
    setUploading(false);
    if (err) {
      setFormError(err);
      return;
    }
    if (url) {
      setEditing((prev) => (prev ? { ...prev, mediaType: kind, mediaUrl: url } : prev));
    }
  }

  const activeCount = items.filter((i) => i.isActive).length;
  const hiddenCount = items.length - activeCount;
  const visibleItems = items.filter((item) =>
    filter === 'all' ? true : filter === 'active' ? item.isActive : !item.isActive,
  );

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[900px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Testimonios de la Manada</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Fotos o videos de clientes (capturas de WhatsApp, reseñas) que aparecen en un carrusel en el home, justo después del catálogo.
            </p>
          </div>
          <button
            onClick={() => {
              setEditing(emptyTestimonial(items.length + 1));
              setFormError('');
            }}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo testimonio
          </button>
        </div>

        {/* Encabezado de la sección */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
              <i className="ri-quote-text text-accent-600"></i>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground-900">Encabezado de la sección</h2>
              <p className="text-xs text-foreground-500">El título y el texto que acompañan el carrusel</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className={labelCls}>Título de la sección</label>
              <input
                className={inputCls}
                value={section.title}
                onChange={(e) => setSection({ ...section, title: e.target.value })}
                placeholder="Testimonios de la Manada"
              />
            </div>
            <div>
              <label className={labelCls}>Descripción de la sección</label>
              <textarea
                className={`${inputCls} resize-none`}
                rows={2}
                maxLength={300}
                value={section.subtitle}
                onChange={(e) => setSection({ ...section, subtitle: e.target.value })}
                placeholder="Historias reales de clientes que ya hacen parte de la Patyfamilia."
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={section.enabled}
                onChange={(e) => setSection({ ...section, enabled: e.target.checked })}
                className="w-4 h-4 accent-accent-500 cursor-pointer"
              />
              <span className="text-sm text-foreground-700">
                {section.enabled ? 'Visible en la tienda' : 'Oculta'}
              </span>
            </label>
          </div>
          <div className="px-5 py-4 border-t border-background-100 flex justify-end">
            <button
              onClick={handleSectionSave}
              disabled={savingSection}
              className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
            >
              {savingSection ? 'Guardando...' : 'Guardar encabezado'}
            </button>
          </div>
        </div>

        {feedback && (
          <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-4 py-2.5">
            <i className="ri-check-line"></i>{feedback}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2.5">
            <i className="ri-error-warning-line"></i>{error}
            <button onClick={load} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Reintentar</button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="inline-flex items-center gap-1 rounded-full bg-background-100 border border-background-200 p-1">
            {([
              ['all', 'Todos', items.length],
              ['active', 'Activos', activeCount],
              ['hidden', 'Ocultos', hiddenCount],
            ] as const).map(([value, label, count]) => (
              <button
                key={value}
                type="button"
                onClick={() => setFilter(value)}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  filter === value
                    ? 'bg-white text-foreground-900 border border-background-200'
                    : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {label}
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    filter === value ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'
                  }`}
                >
                  {count}
                </span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 text-center py-16 text-foreground-500">
            <i className="ri-image-2-line text-4xl block mb-3"></i>
            <p className="text-sm">Aún no hay testimonios. Sube el primero.</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 text-center py-16 text-foreground-500">
            <i className="ri-filter-3-line text-4xl block mb-3"></i>
            <p className="text-sm">No hay testimonios en este filtro.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleItems.map((item) => {
              const fullIdx = items.findIndex((x) => x.id === item.id);
              return (
              <div key={item.id} className="bg-white rounded-xl border border-background-200 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex flex-col gap-1 pt-0.5">
                    <button
                      onClick={() => move(item.id, -1)}
                      disabled={fullIdx === 0}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                      aria-label="Subir"
                    >
                      <i className="ri-arrow-up-line"></i>
                    </button>
                    <button
                      onClick={() => move(item.id, 1)}
                      disabled={fullIdx === items.length - 1}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 disabled:opacity-30 cursor-pointer"
                      aria-label="Bajar"
                    >
                      <i className="ri-arrow-down-line"></i>
                    </button>
                  </div>

                  <div className="w-16 h-20 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                    {item.mediaType === 'video' ? (
                      <div className="w-full h-full flex items-center justify-center bg-black">
                        <i className="ri-play-circle-line text-white text-2xl"></i>
                      </div>
                    ) : (
                      <img src={item.mediaUrl} alt="Testimonio" className="w-full h-full object-cover object-top" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-semibold text-accent-600 uppercase tracking-wide">
                        {item.mediaType === 'video' ? 'Video' : 'Foto'}
                      </span>
                      {!item.isActive && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">Oculto</span>
                      )}
                    </div>
                    {item.clientName && (
                      <p className="text-sm font-medium text-foreground-900 mt-1">{item.clientName}</p>
                    )}
                    {item.caption && (
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{item.caption}</p>
                    )}
                    {item.productName && (
                      <span className="inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium">
                        <i className="ri-shopping-bag-3-line text-secondary-600"></i>
                        Compró: {item.productName}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() =>
                        setEditing({
                          id: item.id,
                          mediaType: item.mediaType,
                          mediaUrl: item.mediaUrl,
                          caption: item.caption,
                          clientName: item.clientName,
                          productName: item.productName,
                          productSlug: item.productSlug,
                          reply: item.reply,
                          displayOrder: item.displayOrder,
                          isActive: item.isActive,
                        })
                      }
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                      aria-label="Editar"
                    >
                      <i className="ri-edit-line"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(item.id, item.mediaUrl)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                      aria-label="Eliminar"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing.id ? 'Editar testimonio' : 'Nuevo testimonio'}
          subtitle="Sube una foto o video y escribe el mensaje del cliente y nuestra respuesta."
          onClose={() => setEditing(null)}
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>{formError}
              </div>
            )}

            <div>
              <label className={labelCls}>Tipo de contenido</label>
              <div className="flex gap-2">
                {([
                  ['image', 'Foto', 'ri-image-line'],
                  ['video', 'Video', 'ri-video-line'],
                ] as const).map(([value, text, icon]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setEditing({ ...editing, mediaType: value })}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      editing.mediaType === value
                        ? 'border-accent-500 bg-accent-50 text-accent-700'
                        : 'border-background-300 bg-white text-foreground-600 hover:border-background-400'
                    }`}
                  >
                    <i className={icon}></i>
                    {text}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>
                {editing.mediaType === 'video' ? 'Video (captura o reseña)' : 'Foto / captura de WhatsApp'} *
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={editing.mediaUrl}
                  onChange={(e) => setEditing({ ...editing, mediaUrl: e.target.value })}
                  placeholder={editing.mediaType === 'video' ? 'https://... (.mp4)' : 'https://... (imagen)'}
                  className="flex-1 text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                />
                <label className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors text-xs font-medium cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-2-line"></i>
                  {uploading ? 'Subiendo...' : editing.mediaType === 'video' ? 'Subir video' : 'Subir foto'}
                  <input
                    type="file"
                    accept={editing.mediaType === 'video' ? 'video/*' : 'image/*'}
                    className="hidden"
                    onChange={(e) => handleMediaUpload(e, editing.mediaType)}
                    disabled={uploading}
                  />
                </label>
              </div>
              {editing.mediaUrl && (
                <div className="mt-2 w-full max-w-[180px] h-40 rounded-lg border border-background-200 overflow-hidden bg-background-100">
                  {editing.mediaType === 'video' ? (
                    <video src={editing.mediaUrl} controls muted playsInline className="w-full h-full object-cover bg-black" />
                  ) : (
                    <img src={editing.mediaUrl} alt="" className="w-full h-full object-cover object-top" />
                  )}
                </div>
              )}
            </div>

            <div>
              <label className={labelCls}>Mensaje del cliente</label>
              <textarea
                className={`${inputCls} resize-none`}
                rows={3}
                maxLength={500}
                value={editing.caption}
                onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                placeholder="Ej: ¡Amé mis babuchas, súper cómodas! Gracias."
              />
            </div>

            <div>
              <label className={labelCls}>Nombre / ciudad del cliente (opcional)</label>
              <input
                className={inputCls}
                value={editing.clientName}
                onChange={(e) => setEditing({ ...editing, clientName: e.target.value })}
                placeholder="Ej: Carolina M. - Bogotá"
              />
            </div>

            <div>
              <label className={labelCls}>Producto que compró (opcional)</label>
              <select
                className={inputCls}
                value={editing.productSlug}
                onChange={(e) => {
                  const slug = e.target.value;
                  const prod = products.find((p) => p.slug === slug);
                  setEditing({ ...editing, productSlug: slug, productName: prod ? prod.name : '' });
                }}
              >
                <option value="">— Sin producto vinculado —</option>
                {editing.productSlug &&
                  !products.some((p) => p.slug === editing.productSlug) && (
                    <option value={editing.productSlug}>
                      {editing.productName || editing.productSlug}
                    </option>
                  )}
                {products.map((p) => (
                  <option key={p.id} value={p.slug}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-foreground-400 mt-1.5">
                Se muestra como &quot;Compró: ...&quot; en la tarjeta y enlaza directo a la ficha de ese producto en la tienda.
              </p>
            </div>

            <div>
              <label className={labelCls}>Nuestra respuesta</label>
              <textarea
                className={`${inputCls} resize-none`}
                rows={2}
                maxLength={300}
                value={editing.reply}
                onChange={(e) => setEditing({ ...editing, reply: e.target.value })}
                placeholder="Ej: ¡Bienvenido a la manada! Gracias por confiar."
              />
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                className="w-4 h-4 accent-accent-500 cursor-pointer"
              />
              <span className="text-sm text-foreground-700">
                {editing.isActive ? 'Visible en la tienda' : 'Oculto'}
              </span>
            </label>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving || uploading}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}